import React, { useState, useEffect } from 'react';
import './SystemGuide.css';

function SystemGuide({ isActive, message, systemState, currentStep, totalSteps, onNext, onPrevious, onExit }) {
  const [isVisible, setIsVisible] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  // Control visibility with smooth fade-in
  useEffect(() => {
    if (isActive) {
      // Delay appearance slightly for dramatic effect
      const timer = setTimeout(() => setIsVisible(true), 300);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setShowMessage(false);
    }
  }, [isActive]);

  // Handle message display timing - no auto-dismiss, stays until step change
  useEffect(() => {
    if (message && isVisible) {
      // Show message immediately when it changes
      setShowMessage(true);
    } else {
      setShowMessage(false);
    }
  }, [message, isVisible, systemState]); // Re-trigger when systemState changes

  if (!isActive && !isVisible) return null;

  return (
    <div className={`system-guide ${isVisible ? 'visible' : ''} ${showMessage ? 'speaking' : ''}`}>
      {/* Geometric Hologram Entity */}
      <div className="guide-entity">
        <svg viewBox="0 0 200 200" className="geometric-form">
          {/* Background glow */}
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            
            <linearGradient id="hologramGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{stopColor: '#00ff9d', stopOpacity: 0.8}} />
              <stop offset="50%" style={{stopColor: '#06b6d4', stopOpacity: 0.9}} />
              <stop offset="100%" style={{stopColor: '#00ff9d', stopOpacity: 0.8}} />
            </linearGradient>
          </defs>

          {/* Central rotating polygon cluster */}
          <g className="polygon-cluster">
            {/* Main hexagon */}
            <polygon 
              points="100,30 140,55 140,105 100,130 60,105 60,55" 
              fill="none" 
              stroke="url(#hologramGradient)" 
              strokeWidth="1.5"
              filter="url(#glow)"
              className="shape shape-1"
            />
            
            {/* Inner triangle */}
            <polygon 
              points="100,60 120,90 80,90" 
              fill="none" 
              stroke="#00ff9d" 
              strokeWidth="1"
              className="shape shape-2"
            />
            
            {/* Outer fragments */}
            <polygon 
              points="100,20 110,25 105,35" 
              fill="none" 
              stroke="#06b6d4" 
              strokeWidth="1"
              className="shape shape-3 fragment"
            />
            
            <polygon 
              points="150,55 155,65 145,70" 
              fill="none" 
              stroke="#06b6d4" 
              strokeWidth="1"
              className="shape shape-4 fragment"
            />
            
            <polygon 
              points="145,110 150,120 140,115" 
              fill="none" 
              stroke="#06b6d4" 
              strokeWidth="1"
              className="shape shape-5 fragment"
            />
            
            <polygon 
              points="55,110 50,120 60,115" 
              fill="none" 
              stroke="#06b6d4" 
              strokeWidth="1"
              className="shape shape-6 fragment"
            />

            {/* Corner nodes */}
            <circle cx="100" cy="30" r="2" fill="#00ff9d" className="node node-1"/>
            <circle cx="140" cy="55" r="2" fill="#00ff9d" className="node node-2"/>
            <circle cx="140" cy="105" r="2" fill="#00ff9d" className="node node-3"/>
            <circle cx="100" cy="130" r="2" fill="#00ff9d" className="node node-4"/>
            <circle cx="60" cy="105" r="2" fill="#00ff9d" className="node node-5"/>
            <circle cx="60" cy="55" r="2" fill="#00ff9d" className="node node-6"/>

            {/* Center pulse */}
            <circle cx="100" cy="80" r="3" fill="none" stroke="#00ff9d" strokeWidth="1" className="center-pulse"/>
          </g>

          {/* Scan lines */}
          <line x1="40" y1="80" x2="160" y2="80" stroke="#06b6d4" strokeWidth="0.5" opacity="0.4" className="scan-line"/>
        </svg>

        {/* Glitch overlay */}
        <div className="glitch-overlay"></div>
      </div>

      {/* Message Container */}
      {message && (
        <div className={`guide-message ${showMessage ? 'visible' : ''}`}>
          {/* Corner Exit Button */}
          <button 
            className="corner-exit" 
            onClick={onExit}
            style={{ pointerEvents: 'auto' }}
            title="Exit Tour (Esc)"
          >
            ×
          </button>

          <div className="message-header">[ SYSTEM CONTEXT ]</div>
          {systemState && <div className="system-state">{systemState}</div>}
          <div className="message-text">{message}</div>
          
          {/* Navigation Button - Continue or Complete */}
          {currentStep < totalSteps - 1 ? (
            <button 
              className="continue-btn" 
              onClick={onNext}
              style={{ pointerEvents: 'auto' }}
            >
              Continue ▶
            </button>
          ) : (
            <button 
              className="continue-btn complete-btn" 
              onClick={onExit}
              style={{ pointerEvents: 'auto' }}
            >
              Complete Tour ✓
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SystemGuide;
